/**
 * Worker-based Image Cleaner Utility
 *
 * Provides an interface to the image processing Web Worker.
 * Handles Canvas API operations on main thread (Canvas requires DOM).
 *
 * Key features:
 * - Analyze image to detect watermark + extract metadata
 * - Remove watermark using reverse alpha blending
 * - Strip metadata via Canvas API redraw
 */

import type {
  WorkerMessage,
  WorkerResponse,
  WatermarkInfo,
  WatermarkProfile,
  WatermarkSize,
  MetadataInfo,
  SupportedImageFormat,
} from '../workers/imageProcessing.types';
import { extractMetadata } from './imageMetadata';

// ============================================================================
// Worker Management
// ============================================================================

let worker: Worker | null = null;
let workerInitialized = false;
let alphaMapLoaded: Record<WatermarkProfile, Record<WatermarkSize, boolean>> = {
  current: { 48: false, 96: false },
  legacy: { 48: false, 96: false },
};
let requestIdCounter = 0;

interface PendingRequest<T> {
  resolve: (result: T) => void;
  reject: (error: Error) => void;
  onProgress?: (message: string, progress: number) => void;
}

const pendingRequests = new Map<string, PendingRequest<unknown>>();

/**
 * Generate unique request ID
 */
function generateRequestId(): string {
  return `img_req_${++requestIdCounter}_${Date.now()}`;
}

/**
 * Dynamic import of file-saver for client-side only
 */
const getSaveAs = async () => {
  if (typeof window === 'undefined') {
    throw new Error('File saving is only available in the browser');
  }
  const fileSaver = await import('file-saver');
  return fileSaver.saveAs || (fileSaver as unknown as { default: { saveAs: typeof fileSaver.saveAs } }).default?.saveAs || (fileSaver as unknown as { default: typeof fileSaver.saveAs }).default;
};

/**
 * Initialize the Web Worker (client-side only)
 */
function initWorker(): void {
  if (workerInitialized || typeof window === 'undefined') {
    return;
  }

  try {
    worker = new Worker(
      new URL('../workers/imageProcessing.worker.ts', import.meta.url),
      { type: 'module' }
    );

    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const message = e.data;

      const pending = pendingRequests.get(message.requestId);
      if (!pending) return;

      switch (message.type) {
        case 'PROGRESS':
          pending.onProgress?.(message.message, message.progress);
          break;

        case 'ANALYSIS_RESULT':
          (pending as PendingRequest<WatermarkInfo>).resolve(message.watermark);
          pendingRequests.delete(message.requestId);
          break;

        case 'PROCESS_RESULT':
          (pending as PendingRequest<{ imageData: ImageData; watermarkRemoved: boolean }>).resolve({
            imageData: message.processedData,
            watermarkRemoved: message.watermarkRemoved,
          });
          pendingRequests.delete(message.requestId);
          break;

        case 'ALPHA_MAP_LOADED':
          alphaMapLoaded[message.profile][message.size] = true;
          (pending as PendingRequest<void>).resolve(undefined);
          pendingRequests.delete(message.requestId);
          break;

        case 'ERROR':
          pending.reject(new Error(message.error));
          pendingRequests.delete(message.requestId);
          break;
      }
    };

    worker.onerror = (error) => {
      console.error('[ImageWorker] Worker error:', error);
      pendingRequests.forEach(({ reject }) => {
        reject(new Error('Image processing worker error'));
      });
      pendingRequests.clear();
    };

    workerInitialized = true;
  } catch (error) {
    console.error('[ImageWorker] Failed to initialize worker:', error);
    throw new Error('Failed to initialize image processing');
  }
}

/**
 * Send message to worker and await response
 */
function sendWorkerMessage<T>(
  message: WorkerMessage,
  onProgress?: (message: string, progress: number) => void,
  timeoutMs = 30000
): Promise<T> {
  return new Promise((resolve, reject) => {
    if (!workerInitialized) {
      initWorker();
    }

    if (!worker) {
      reject(new Error('Worker not available'));
      return;
    }

    pendingRequests.set(message.requestId, {
      resolve: resolve as (result: unknown) => void,
      reject,
      onProgress,
    });

    // Timeout to prevent hanging
    const timeout = setTimeout(() => {
      const pending = pendingRequests.get(message.requestId);
      if (pending) {
        pending.reject(new Error(`Image processing timeout (${timeoutMs / 1000}s)`));
        pendingRequests.delete(message.requestId);
      }
    }, timeoutMs);

    // Clear timeout when resolved
    const originalResolve = (pendingRequests.get(message.requestId) as PendingRequest<T>).resolve;
    (pendingRequests.get(message.requestId) as PendingRequest<T>).resolve = (result: T) => {
      clearTimeout(timeout);
      originalResolve(result);
    };

    worker.postMessage(message);
  });
}

// ============================================================================
// Alpha Map Loading
// ============================================================================

/**
 * Load alpha map template from public assets
 */
async function loadAlphaMap(
  profile: WatermarkProfile,
  size: WatermarkSize
): Promise<void> {
  if (alphaMapLoaded[profile][size]) return;

  const img = new Image();
  const filename = profile === 'current'
    ? `gemini-v2-${size}.png`
    : `gemini-${size}.png`;
  img.src = `/watermark-templates/${filename}`;

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load alpha map: ${filename}`));
  });

  // Draw to canvas to get pixel data
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, size, size);

  // Send to worker
  await sendWorkerMessage<void>({
    type: 'LOAD_ALPHA_MAP',
    profile,
    size,
    alphaMapData: imageData.data,
    requestId: generateRequestId(),
  });
}

/**
 * Ensure all alpha maps are loaded
 */
async function ensureAlphaMapsLoaded(): Promise<void> {
  const promises: Promise<void>[] = [];

  for (const profile of ['current', 'legacy'] as const) {
    for (const size of [48, 96] as const) {
      if (!alphaMapLoaded[profile][size]) {
        promises.push(loadAlphaMap(profile, size));
      }
    }
  }

  await Promise.all(promises);
}

// ============================================================================
// Image Processing
// ============================================================================

/**
 * Load image file into Canvas and get ImageData
 */
async function loadImageToCanvas(
  file: File
): Promise<{ imageData: ImageData; canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D }> {
  const img = new Image();
  const objectUrl = URL.createObjectURL(file);

  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = objectUrl;
    });

    // Create canvas and draw image (strips EXIF metadata)
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, img.width, img.height);

    return { imageData, canvas, ctx };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// ============================================================================
// Public API
// ============================================================================

export interface AnalysisResult {
  watermark: WatermarkInfo;
  metadata: MetadataInfo;
  previewUrl: string; // Object URL for preview with highlighted watermark
  originalUrl: string; // Object URL for original image
  width: number;
  height: number;
}

export interface ProcessResult {
  cleanedBlob: Blob; // Cleaned image blob (not yet downloaded)
  cleanedUrl: string; // Object URL for cleaned image preview
  originalUrl: string; // Object URL for original image preview
  filename: string; // Suggested download filename
  watermarkRemoved: boolean; // True only if a watermark was actually detected and removed
}

/**
 * Validate if file is a supported image
 */
export function isValidImageFile(file: File): boolean {
  const validTypes: SupportedImageFormat[] = ['image/jpeg', 'image/png', 'image/webp'];
  const validExtensions = ['.jpg', '.jpeg', '.png', '.webp'];

  // Check MIME type
  if (validTypes.includes(file.type as SupportedImageFormat)) {
    return true;
  }

  // Fallback to extension check
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  return validExtensions.includes(ext);
}

/**
 * Analyze an image to detect watermark and extract metadata.
 * Returns preview with watermark region highlighted.
 *
 * @param file - Image file to analyze
 * @param onProgress - Progress callback
 * @returns Analysis result with watermark info and metadata
 */
export async function analyzeImage(
  file: File,
  onProgress?: (message: string) => void
): Promise<AnalysisResult> {
  if (!isValidImageFile(file)) {
    throw new Error('Unsupported image format. Please use JPEG, PNG, or WebP.');
  }

  onProgress?.('Loading image...');

  // Load alpha maps first
  await ensureAlphaMapsLoaded();

  // Load image to canvas
  const { imageData, canvas, ctx } = await loadImageToCanvas(file);

  onProgress?.('Extracting metadata...');

  // Extract metadata using ExifReader
  const metadata = await extractMetadata(file);

  // Update dimensions from actual image
  metadata.dimensions = { width: canvas.width, height: canvas.height };

  onProgress?.('Detecting watermark...');

  // Analyze watermark in worker
  const watermark = await sendWorkerMessage<WatermarkInfo>(
    {
      type: 'ANALYZE_IMAGE',
      imageData,
      width: canvas.width,
      height: canvas.height,
      requestId: generateRequestId(),
    },
    (msg) => onProgress?.(msg)
  );

  // Create preview with watermark highlighted - LARGE & READABLE
  if (watermark.detected && watermark.region) {
    const { x, y, size } = watermark.region;

    // Scale highlight based on image size for visibility
    const scaleFactor = Math.max(1, Math.min(canvas.width, canvas.height) / 400);
    const lineWidth = Math.max(6, 4 * scaleFactor);
    const fontSize = Math.max(18, 14 * scaleFactor);
    const padding = Math.max(8, 6 * scaleFactor);

    // Draw outer glow effect
    ctx.strokeStyle = 'rgba(255, 107, 53, 0.3)';
    ctx.lineWidth = lineWidth + 8;
    ctx.setLineDash([]);
    ctx.strokeRect(x - padding, y - padding, size + padding * 2, size + padding * 2);

    // Draw main highlight box - solid thick border
    ctx.strokeStyle = '#FF6B35';
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([]);
    ctx.strokeRect(x - padding/2, y - padding/2, size + padding, size + padding);

    // Draw inner dashed border for extra visibility
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]);
    ctx.strokeRect(x, y, size, size);

    // Draw label with larger text
    ctx.setLineDash([]);
    ctx.fillStyle = '#FF6B35';
    ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`;
    const labelText = `⚠️ Watermark ${size}×${size}px`;
    const labelWidth = ctx.measureText(labelText).width + 24;
    const labelHeight = fontSize + 16;
    const labelX = Math.min(x, canvas.width - labelWidth - 10);
    const labelY = Math.max(y - labelHeight - 10, 10);

    // Label background with rounded corners effect
    ctx.fillStyle = '#FF6B35';
    ctx.beginPath();
    ctx.roundRect(labelX, labelY, labelWidth, labelHeight, 6);
    ctx.fill();

    // Label text
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(labelText, labelX + 12, labelY + fontSize + 4);

    // Add corner markers for extra visibility
    const cornerSize = Math.max(12, 8 * scaleFactor);
    ctx.strokeStyle = '#FF6B35';
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([]);

    // Top-left corner
    ctx.beginPath();
    ctx.moveTo(x - padding, y - padding + cornerSize);
    ctx.lineTo(x - padding, y - padding);
    ctx.lineTo(x - padding + cornerSize, y - padding);
    ctx.stroke();

    // Top-right corner
    ctx.beginPath();
    ctx.moveTo(x + size + padding - cornerSize, y - padding);
    ctx.lineTo(x + size + padding, y - padding);
    ctx.lineTo(x + size + padding, y - padding + cornerSize);
    ctx.stroke();

    // Bottom-left corner
    ctx.beginPath();
    ctx.moveTo(x - padding, y + size + padding - cornerSize);
    ctx.lineTo(x - padding, y + size + padding);
    ctx.lineTo(x - padding + cornerSize, y + size + padding);
    ctx.stroke();

    // Bottom-right corner
    ctx.beginPath();
    ctx.moveTo(x + size + padding - cornerSize, y + size + padding);
    ctx.lineTo(x + size + padding, y + size + padding);
    ctx.lineTo(x + size + padding, y + size + padding - cornerSize);
    ctx.stroke();
  }

  // Create object URLs
  const previewBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Failed to create preview'))), 'image/png');
  });

  const previewUrl = URL.createObjectURL(previewBlob);
  const originalUrl = URL.createObjectURL(file);

  return {
    watermark,
    metadata,
    previewUrl,
    originalUrl,
    width: canvas.width,
    height: canvas.height,
  };
}

/**
 * Process a Gemini image: remove watermark and strip metadata.
 * Does NOT download — returns the cleaned blob for preview.
 * Call downloadCleanedImage() separately to trigger the download.
 */
export async function processGeminiImage(
  file: File,
  onProgress?: (message: string) => void
): Promise<ProcessResult> {
  if (!isValidImageFile(file)) {
    throw new Error('Unsupported image format. Please use JPEG, PNG, or WebP.');
  }

  onProgress?.('Preparing...');
  await ensureAlphaMapsLoaded();

  onProgress?.('Loading image...');
  const { imageData, canvas, ctx } = await loadImageToCanvas(file);

  onProgress?.('Processing image...');
  const { imageData: processedData, watermarkRemoved } = await sendWorkerMessage<{
    imageData: ImageData;
    watermarkRemoved: boolean;
  }>(
    {
      type: 'PROCESS_IMAGE',
      imageData,
      width: canvas.width,
      height: canvas.height,
      removeWatermark: true,
      outputFormat: (file.type as SupportedImageFormat) || 'image/png',
      outputQuality: 0.92,
      requestId: generateRequestId(),
    },
    (msg) => onProgress?.(msg)
  );

  onProgress?.('Generating cleaned image...');
  ctx.putImageData(processedData, 0, 0);

  const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
  const outputType = validTypes.includes(file.type) ? file.type : 'image/png';
  const quality = outputType === 'image/png' ? undefined : 0.92;

  const cleanedBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to create cleaned image'))),
      outputType,
      quality
    );
  });

  const originalName = file.name.replace(/\.[^.]+$/, '');
  const extension = file.name.split('.').pop() || 'png';
  const filename = `${originalName}_cleaned.${extension}`;

  const cleanedUrl = URL.createObjectURL(cleanedBlob);
  const originalUrl = URL.createObjectURL(file);

  return { cleanedBlob, cleanedUrl, originalUrl, filename, watermarkRemoved };
}

/**
 * Download a previously processed cleaned image.
 */
export async function downloadCleanedImage(
  cleanedBlob: Blob,
  filename: string
): Promise<void> {
  const saveAs = await getSaveAs();
  saveAs(cleanedBlob, filename);
}

/**
 * Terminate worker and cleanup
 */
export function terminateImageWorker(): void {
  if (worker) {
    worker.terminate();
    worker = null;
    workerInitialized = false;
    alphaMapLoaded = {
      current: { 48: false, 96: false },
      legacy: { 48: false, 96: false },
    };
    pendingRequests.clear();
  }
}

/**
 * Revoke object URLs to free memory
 */
export function revokeUrls(...urls: (string | undefined)[]): void {
  urls.forEach((url) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  });
}

/**
 * Get file size in human-readable format
 */
export function getReadableFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}
